import React, { createContext, useContext, useReducer, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import API_CONFIG from '../config/apiConfig';

// Configure axios with the correct base URL
axios.defaults.baseURL = API_CONFIG.BASE_URL;

const AuthContext = createContext();

const initialState = {
  user: null,
  token: localStorage.getItem('token'),
  isAuthenticated: !!localStorage.getItem('token'),
  loading: !!localStorage.getItem('token')
};

const authReducer = (state, action) => {
  console.log('🔍 AuthContext: Reducer called with action:', action.type, 'payload:', action.payload);
  
  switch (action.type) {
    case 'AUTH_START':
      console.log('🔄 AuthContext: Setting loading to true');
      return {
        ...state,
        loading: true
      };
    case 'AUTH_SUCCESS':
      console.log('✅ AuthContext: AUTH_SUCCESS - setting user and token');
      console.log('✅ AuthContext: User payload:', action.payload.user);
      console.log('✅ AuthContext: Zoho data in payload:', action.payload.user?.zohoData);
      
      const newState = {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isAuthenticated: true,
        loading: false
      };
      console.log('✅ AuthContext: New state:', newState);
      console.log('✅ AuthContext: User in new state:', newState.user);
      console.log('✅ AuthContext: Zoho data in new state:', newState.user?.zohoData);
      return newState;
    case 'AUTH_FAIL':
      console.log('❌ AuthContext: AUTH_FAIL - clearing user and token');
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        loading: false
      };
    case 'LOGOUT':
      console.log('🚪 AuthContext: LOGOUT - clearing user and token');
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        loading: false
      };
    case 'UPDATE_USER':
      console.log('📝 AuthContext: UPDATE_USER - updating user data');
      return {
        ...state,
        user: { ...state.user, ...action.payload }
      };
    case 'SET_LOADING_FALSE':
      console.log('🔄 AuthContext: Setting loading to false');
      return {
        ...state,
        loading: false
      };
    default:
      return state;
  }
};

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Utility function to validate token
  const validateToken = async (token) => {
    try {
      const res = await axios.get('/api/auth/me', {
        headers: { 'x-auth-token': token }
      });
      return { valid: true, user: res.data };
    } catch (error) {
      console.log('🔍 Token validation failed:', error.response?.status);
      return { 
        valid: false, 
        error: error.response?.status,
        message: error.response?.data?.message || 'Token validation failed'
      };
    }
  };

  // Utility function to refresh token if possible
  const attemptTokenRefresh = async (token) => {
    try {
      const res = await axios.post('/api/auth/refresh-token', {}, {
        headers: { 'x-auth-token': token }
      });
      return { success: true, newToken: res.data.token };
    } catch (error) {
      console.log('🔍 Token refresh failed:', error.response?.status);
      return { success: false, error: error.response?.status };
    }
  };

  // Set axios default header
  useEffect(() => {
    console.log('🔍 AuthContext: Token changed, current token:', state.token);
    console.log('🔍 AuthContext: Current state:', state);
    if (state.token) {
      console.log('✅ AuthContext: Setting axios header and localStorage token');
      axios.defaults.headers.common['x-auth-token'] = state.token;
      localStorage.setItem('token', state.token);
      localStorage.setItem('tokenTimestamp', Date.now().toString());
      console.log('✅ AuthContext: Token and timestamp stored in localStorage');
    } else {
      console.log('❌ AuthContext: Clearing axios header and localStorage token');
      delete axios.defaults.headers.common['x-auth-token'];
      localStorage.removeItem('token');
      localStorage.removeItem('tokenTimestamp');
      console.log('✅ AuthContext: Token and timestamp removed from localStorage');
    }
  }, [state.token]);

  // Load user on mount
  useEffect(() => {
    console.log('🔍 AuthContext: useEffect triggered, current state:', state);
    const loadUser = async () => {
      if (state.token) {
        console.log('🔍 AuthContext: Token exists, validating token...');
        
        // Check if token is expired by checking localStorage timestamp
        const tokenTimestamp = localStorage.getItem('tokenTimestamp');
        const tokenAge = tokenTimestamp ? Date.now() - parseInt(tokenTimestamp) : 0;
        const maxTokenAge = 24 * 60 * 60 * 1000; // 24 hours
        
        if (tokenAge > maxTokenAge) {
          console.log('⏰ AuthContext: Token is too old, clearing it');
          localStorage.removeItem('token');
          localStorage.removeItem('tokenTimestamp');
          dispatch({ type: 'AUTH_FAIL' });
          return;
        }
        
        // First, try to validate the token
        const validation = await validateToken(state.token);
        
        if (validation.valid) {
          console.log('✅ AuthContext: Token is valid, user data loaded:', validation.user);

          // Restore full zohoData: in-memory state > localStorage > minimal DB data
          let restoredZohoData = state.user?.zohoData || validation.user.zohoData || null;
          if (!restoredZohoData?.formData) {
            try {
              const stored = localStorage.getItem('zohoCRMData');
              if (stored) {
                const parsed = JSON.parse(stored);
                restoredZohoData = parsed;
                console.log('✅ AuthContext: Restored full Zoho data from localStorage');
              }
            } catch (e) {
              console.warn('⚠️ AuthContext: Failed to parse zohoCRMData from localStorage');
            }
          }

          const userWithZohoData = {
            ...validation.user,
            zohoData: restoredZohoData
          };

          console.log('✅ AuthContext: User data with preserved Zoho data:', userWithZohoData);

          dispatch({
            type: 'AUTH_SUCCESS',
            payload: { user: userWithZohoData, token: state.token }
          });
        } else {
          console.log('❌ AuthContext: Token validation failed:', validation.error);
          
          // Check if this is a backend connection issue in development
          const isBackendUnavailable = (
            validation.error === undefined || // No response
            validation.error >= 500 || // Server error
            validation.message?.includes('Network Error') ||
            validation.message?.includes('Failed to fetch') ||
            validation.message?.includes('ECONNREFUSED')
          );

          if (isBackendUnavailable && process.env.NODE_ENV === 'development') {
            console.log('🔄 AuthContext: Backend unavailable in dev mode, using mock user');
            const mockUser = {
              id: 'dev-user-id',
              firstName: 'Development',
              lastName: 'User',
              email: 'dev@example.com',
              mobile: '+919876543210',
              role: 'parent',
              userType: 'parent'
            };
            
            dispatch({
              type: 'AUTH_SUCCESS',
              payload: { user: mockUser, token: state.token }
            });
            return;
          }
          
          // If token is invalid (401), try to refresh it
          if (validation.error === 401) {
            console.log('🔄 AuthContext: Attempting token refresh...');
            const refreshResult = await attemptTokenRefresh(state.token);
            
            if (refreshResult.success) {
              console.log('✅ AuthContext: Token refreshed successfully');
              // Try validation again with new token
              const newValidation = await validateToken(refreshResult.newToken);
              if (newValidation.valid) {
                dispatch({
                  type: 'AUTH_SUCCESS',
                  payload: { user: newValidation.user, token: refreshResult.newToken }
                });
                return;
              }
            }
          }
          
          // If refresh failed or token is still invalid, clear everything
          console.log('🔒 AuthContext: Token invalid and refresh failed, clearing token');
          localStorage.removeItem('token');
          localStorage.removeItem('tokenTimestamp');
          dispatch({ type: 'AUTH_FAIL' });
          
          // Show user-friendly message
          if (validation.error === 401) {
            toast.error('Your session has expired. Please log in again.');
          } else {
            toast.error('Unable to verify login. Please try logging in again.');
          }
        }
      } else {
        console.log('🔍 AuthContext: No token, setting loading to false');
        // Just set loading to false, don't change authentication state
        dispatch({ type: 'SET_LOADING_FALSE' });
      }
    };

    loadUser();
  }, []);

  // Register user
  const register = async (userData) => {
    dispatch({ type: 'AUTH_START' });
    try {
      const res = await axios.post('/api/auth/register', userData);
      dispatch({
        type: 'AUTH_SUCCESS',
        payload: res.data
      });
      toast.success('Registration successful!');
      return { success: true };
    } catch (error) {
      // Fallback - simulate successful registration
      if (process.env.NODE_ENV === 'development') {
        // Check if this is a backend connection issue
        const isBackendUnavailable = (
          error.code === 'ERR_NETWORK' || 
          error.code === 'ERR_CONNECTION_REFUSED' ||
          error.message?.includes('Network Error') ||
          error.message?.includes('Failed to fetch') ||
          error.message?.includes('ECONNREFUSED') ||
          error.response?.status >= 500 ||
          !error.response // No response means backend is unreachable
        );

        if (isBackendUnavailable) {
          
          // Create mock user data
          const mockUser = {
            id: 'dev-registered-user-id',
            firstName: userData.firstName || 'Test',
            lastName: userData.lastName || 'User',
            email: userData.email,
            mobile: userData.mobile,
            role: 'parent',
            userType: 'parent'
          };

          dispatch({
            type: 'AUTH_SUCCESS',
            payload: { user: mockUser, token: 'dev-token-' + Date.now() }
          });
          
          toast.success('Registration successful!');
          return { success: true };
        }
        
        // Additional fallback: simulate success for development
        
        // Create mock user data
        const mockUser = {
          id: 'dev-registered-user-id',
          firstName: userData.firstName || 'Test',
          lastName: userData.lastName || 'User',
          email: userData.email,
          mobile: userData.mobile,
          role: 'parent',
          userType: 'parent'
        };

        dispatch({
          type: 'AUTH_SUCCESS',
          payload: { user: mockUser, token: 'dev-token-' + Date.now() }
        });
        
        toast.success('Registration successful!');
        return { success: true };
      }

      const message = error.response?.data?.error || 'Registration failed';
      toast.error(message);
      dispatch({ type: 'AUTH_FAIL' });
      return { success: false, error: message };
    }
  };

  // Send OTP to mobile number
  const sendOtp = async (countryCode, mobileNumber) => {
    try {
      const res = await axios.post('/api/auth/send-otp', { 
        countryCode, 
        mobileNumber,
        mobile: `${countryCode}${mobileNumber}` // Keep backward compatibility
      });
      
      // Show appropriate message based on OTP sending status
      if (res.data.otpSent) {
        toast.success('OTP sent successfully!');
      } else {
        toast.success('OTP generated successfully');
      }
      
      return { success: true, data: res.data };
    } catch (error) {
      // Check if it's a phone number not found error - don't show toast for this
      if (error.response?.data?.error === 'PHONE_NOT_FOUND') {
        // Re-throw the error so the Login component can handle it with custom UI
        throw error;
      }
      
      // Fallback - simulate successful OTP sending
      if (process.env.NODE_ENV === 'development') {
        // Check if this is a backend connection issue
        const isBackendUnavailable = (
          error.code === 'ERR_NETWORK' || 
          error.code === 'ERR_CONNECTION_REFUSED' ||
          error.message?.includes('Network Error') ||
          error.message?.includes('Failed to fetch') ||
          error.message?.includes('ECONNREFUSED') ||
          error.response?.status >= 500 ||
          !error.response // No response means backend is unreachable
        );

        if (isBackendUnavailable) {
          toast.success('OTP sent successfully!');
          return { success: true, data: { message: 'OTP sent successfully' } };
        }
        
        // Additional fallback: simulate success
        toast.success('OTP sent successfully!');
        return { success: true, data: { message: 'OTP sent successfully' } };
      }

      const message = error.response?.data?.message || 'Failed to send OTP';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  // Verify OTP and login
  const verifyOtp = async (countryCode, mobileNumber, otp) => {
    dispatch({ type: 'AUTH_START' });
    try {
      const res = await axios.post('/api/auth/verify-otp', { 
        countryCode, 
        mobileNumber, 
        mobile: `${countryCode}${mobileNumber}`, // Keep backward compatibility
        otp 
      });
      
      console.log('📥 AuthContext: Received response from verify-otp:', res.data);
      console.log('📥 AuthContext: User object in response:', res.data.user);
      console.log('📥 AuthContext: Zoho data in response:', res.data.zohoData);

      // zohoData is at the top level of the response (not inside user)
      const freshZohoData = res.data.zohoData || res.data.user?.zohoData || null;

      // Clear old data and store fresh Zoho CRM data
      localStorage.removeItem('zohoCRMData');
      if (freshZohoData) {
        const dataWithTimestamp = { ...freshZohoData, lastUpdated: Date.now() };
        localStorage.setItem('zohoCRMData', JSON.stringify(dataWithTimestamp));
        console.log('✅ AuthContext: Fresh Zoho CRM data stored in localStorage:', dataWithTimestamp);
      } else {
        console.log('⚠️ AuthContext: No Zoho CRM data found in response');
      }

      dispatch({
        type: 'AUTH_SUCCESS',
        payload: {
          ...res.data,
          user: { ...res.data.user, zohoData: freshZohoData }
        }
      });

      console.log('✅ AuthContext: AUTH_SUCCESS dispatched with payload:', res.data);
      toast.success('Login successful!');
      console.log('✅ AuthContext: OTP verification complete, staying on current page');
      
      return { success: true };
    } catch (error) {
      // No development mode bypass - require proper OTP validation

      console.error('❌ AuthContext: OTP verification error:', error);
      
      // Handle specific error cases
      if (error.response?.data?.error === 'OTP_SESSION_EXPIRED') {
        const message = 'OTP session expired. Please request a new OTP.';
        toast.error(message);
        dispatch({ type: 'AUTH_FAIL' });
        return { success: false, error: message, sessionExpired: true };
      } else {
        const message = error.response?.data?.message || 'Invalid OTP';
        toast.error(message);
        dispatch({ type: 'AUTH_FAIL' });
        return { success: false, error: message };
      }
    }
  };

  // Role-based login function
  const login = async (username, password, role = 'parent') => {
    console.log('🚧 Login function called with:', { username, password, role });
    console.log('🚧 NODE_ENV:', process.env.NODE_ENV);
    console.log('🚧 Hostname:', window.location.hostname);
    
    dispatch({ type: 'AUTH_START' });
    try {
      let endpoint = '/api/auth/login';
      let payload = { email: username, password };

      // Route to appropriate endpoint based on role
      if (role === 'student') {
        endpoint = '/api/auth/student-login';
        payload = { username, password };
      } else if (role === 'staff') {
        endpoint = '/api/auth/staff-login';
        payload = { username, password };
      }

      const res = await axios.post(endpoint, payload);
      dispatch({
        type: 'AUTH_SUCCESS',
        payload: res.data
      });
      toast.success('Login successful!');
      return { success: true };
    } catch (error) {
      console.log('🚧 Development Mode: Full error object:', error);
      console.log('🚧 Error code:', error.code);
      console.log('🚧 Error message:', error.message);
      console.log('🚧 Error response:', error.response);
      
      // No development mode bypass - require proper OTP validation

      const message = error.response?.data?.error || 'Login failed';
      toast.error(message);
      dispatch({ type: 'AUTH_FAIL' });
      return { success: false, error: message };
    }
  };

  // Legacy login (kept for backward compatibility)
  const legacyLogin = async (credentials) => {
    dispatch({ type: 'AUTH_START' });
    try {
      const res = await axios.post('/api/auth/login', credentials);
      dispatch({
        type: 'AUTH_SUCCESS',
        payload: res.data
      });
      toast.success('Login successful!');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.error || 'Login failed';
      toast.error(message);
      dispatch({ type: 'AUTH_FAIL' });
      return { success: false, error: message };
    }
  };

  // Logout user
  const logout = () => {
    console.log('🚪 AuthContext: Logout called');
    // Clear all data from localStorage
    localStorage.removeItem('zohoCRMData');
    localStorage.removeItem('token');
    localStorage.removeItem('tokenTimestamp');
    console.log('✅ AuthContext: All data cleared from localStorage');
    dispatch({ type: 'LOGOUT' });
    toast.success('Logged out successfully');
  };

  // Update user profile
  const updateProfile = async (profileData) => {
    try {
      const res = await axios.put('/api/auth/profile', profileData);
      dispatch({
        type: 'UPDATE_USER',
        payload: res.data
      });
      toast.success('Profile updated successfully!');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.error || 'Profile update failed';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  // Change password
  const changePassword = async (passwordData) => {
    try {
      await axios.post('/api/auth/change-password', passwordData);
      toast.success('Password changed successfully!');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.error || 'Password change failed';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  // Update user data directly (for refreshing Zoho data)
  const updateUser = (userData) => {
    console.log('🔄 AuthContext: Updating user data directly:', userData);
    dispatch({
      type: 'UPDATE_USER',
      payload: userData
    });
  };

  const value = {
    user: state.user,
    token: state.token,
    isAuthenticated: state.isAuthenticated,
    loading: state.loading,
    register,
    login,
    legacyLogin,
    sendOtp,
    verifyOtp,
    logout,
    updateProfile,
    changePassword,
    updateUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
